import { Dropdown } from '@/components/Dropdown';
import { UpdateTemplateMenuItem } from '@/components/Dropdown/MenuItems/UpdateTemplateMenuItem';
import { ThreeDotsCircle } from '@/components/ThreeDotsCircle';
import { useFlag } from '@/services/featureFlags';
import { IProjectTemplate, TemplateVisibility } from '@d-id/types';
import styled from '@emotion/styled';
import { MenuList } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';

const StyledVideo = styled.video`
    width: 100%;
    height: 100%;
    object-fit: cover;
    box-shadow: 0px 1.32px 1.32px 0px rgba(0, 0, 0, 0.1);
`;

const ScenesCountTag = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    bottom: 8px;
    left: 8px;
    padding: 2px 8px;
    border-radius: 4px;
    background-color: ${({ theme }) => theme.colors.black[100]};
    color: ${({ theme }) => theme.colors.secondary.main};
`;

const Visibility = styled.div<{ visibility: TemplateVisibility }>`
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    bottom: 8px;
    left: 75px;
    padding: 2px 8px;
    border-radius: 4px;
    background-color: ${({ visibility }) => (visibility === TemplateVisibility.SHOW ? '#00C853' : '#FF1744')};
    color: ${({ theme }) => theme.colors.secondary.main};
`;

const TemplateName = styled.div`
    font-size: 14px;
    color: ${({ theme }) => theme.colors.secondary[900]};
    font-weight: 500;
`;

const TemplateContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    cursor: pointer;
    width: 100%;
`;
const TemplatePreview = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
    border-radius: 8px;
    border: 1px solid #e0e0e0;
    overflow: hidden;
`;

const OptionsButtonWrapper = styled.div`
    position: absolute;
    right: 12px;
    top: 12px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    z-index: 2;
`;

const StyledList = styled(MenuList)`
    padding: 0;

    &:focus {
        outline: none;
    }
`;

export const TEMPLATE_WIDTH = 250;

interface Props {
    template: IProjectTemplate & { width: number; height: number; imageHeight: number };
    setSelectedTemplate: (template: IProjectTemplate | null, index: number | null) => void;
    onLayout?: (width: number) => void;
    index: number;
}

export const TemplateTile = ({ template, setSelectedTemplate, onLayout, index }: Props) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const [open, setOpen] = useState<boolean>(false);
    const projectsTemplateAdmin = useFlag('projects_template_admin', false);
    const containerRef = useRef<HTMLDivElement>(null);
    const [isHovered, setIsHovered] = useState<boolean>(false);

    useEffect(() => {
        if (template.previewUrl) {
            const video = document.createElement('video');
            video.src = template.previewUrl;
            video.preload = 'auto';
            video.muted = true;
        }
    }, [template.previewUrl]);

    useEffect(() => {
        if (!videoRef.current) return;
        if (isHovered) {
            videoRef.current.play();
        } else {
            videoRef.current.pause();
            videoRef.current.currentTime = 0;
        }
    }, [isHovered]);

    const handleMouseEnter = useCallback(() => {
        setIsHovered(true);
    }, []);

    const handleMouseLeave = useCallback(() => {
        setIsHovered(false);
    }, []);

    const handleDropdownOpen = useCallback(() => {
        setOpen(true);
        setIsHovered(false);
    }, []);

    const handleClick = () => {
        setSelectedTemplate(template, index);
    };

    const items = [
        {
            label: <UpdateTemplateMenuItem template={template} closeMenu={() => setOpen(false)} />,
        },
    ];

    useEffect(() => {
        if (!onLayout || !containerRef.current) return;

        const observer = new ResizeObserver(entries => {
            const width = entries[0].contentRect.width;
            onLayout(width);
        });

        observer.observe(containerRef.current);

        return () => observer.disconnect();
    }, [onLayout]);

    return (
        <TemplateContainer
            ref={containerRef}
            onClick={handleClick}
            data-testid="template-tile"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}>
            <TemplatePreview>
                <Dropdown
                    closed={!open}
                    onOpen={handleDropdownOpen}
                    onClose={() => setOpen(false)}
                    trigger={
                        projectsTemplateAdmin ? (
                            <OptionsButtonWrapper>
                                <ThreeDotsCircle data-testid={`template-options-three-dots-circle-${template.name}`} isActive={open} />
                            </OptionsButtonWrapper>
                        ) : (
                            <></>
                        )
                    }
                    items={items}
                    renderMenuWrapper={children => <StyledList>{children}</StyledList>}
                />

                <StyledVideo
                    ref={videoRef}
                    src={isHovered ? template.previewUrl : undefined}
                    poster={template.scenesThumbnailsUrls[0]}
                    autoPlay
                    muted
                    loop
                />
                <ScenesCountTag data-testid={`template-tile-scenes-count-${template.scenesCount}`}>
                    {`${template.scenesCount} ${template.scenesCount > 1 ? 'scenes' : 'scene'}`}
                </ScenesCountTag>
                {projectsTemplateAdmin && <Visibility visibility={template.visibility}>{template.visibility}</Visibility>}
            </TemplatePreview>
            <TemplateName data-testid="template-tile-name">{template.name}</TemplateName>
        </TemplateContainer>
    );
};
